import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

interface VarianceChartProps {
  data: {
    title: string;
    subtitle: string;
    data: Array<{ quarter: string; budget: number; actual: number }>;
    netVariance: {
      value: string;
      percentage: string;
      direction: string;
    };
  };
  onDrillDown?: (quarter?: string) => void;
}

const BUDGET_COLOR = "#92DAAA";
const ACTUAL_COLOR = "#ED9A9A";
const FAVORABLE    = "#2EB85C";
const UNFAVORABLE  = "#ED9A9A";

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const budget = payload.find((p: any) => p.dataKey === "budget")?.value ?? 0;
  const actual = payload.find((p: any) => p.dataKey === "actual")?.value ?? 0;
  const diff   = actual - budget;
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip-label" style={{ marginBottom: 6 }}>{label}</p>
      <p style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>
        Budget: <strong style={{ color: BUDGET_COLOR }}>${budget}M</strong>
      </p>
      <p style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>
        Actual: <strong style={{ color: ACTUAL_COLOR }}>${actual}M</strong>
      </p>
      <p style={{ fontSize: 11, fontWeight: 700, color: diff <= 0 ? FAVORABLE : UNFAVORABLE, borderTop: "1px solid #f1f5f9", paddingTop: 4 }}>
        {diff <= 0 ? "▲ Under Budget" : "▼ Over Budget"}: ${Math.abs(parseFloat(diff.toFixed(2)))}M
      </p>
    </div>
  );
};

const EmptyState: React.FC<{ height: number }> = ({ height }) => (
  <div className="chart-empty" style={{ height }}>
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
      <rect x="6" y="28" width="8" height="14" rx="2" fill="#E2E8F0"/>
      <rect x="20" y="18" width="8" height="24" rx="2" fill="#E2E8F0"/>
      <rect x="34" y="22" width="8" height="20" rx="2" fill="#E2E8F0"/>
      <path d="M4 44h40" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
    <p className="chart-empty-text">No data available for the selected filters</p>
  </div>
);

const VarianceChart: React.FC<VarianceChartProps> = ({ data, onDrillDown }) => {
  const isEmpty = !data.data || data.data.length === 0;
  return (
    <div>
      <div className="chart-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <div className="chart-title">{data.title}</div>
          <div className="chart-subtitle">{data.subtitle}</div>
        </div>
        {onDrillDown && (
          <button className="explore-btn" onClick={() => onDrillDown()}>
            Explore
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" style={{ marginLeft: 4 }}>
              <path d="M2.5 5.5h6M6 3l2.5 2.5L6 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      <div className="chart-legend">
        <div className="legend-item">
          <div className="legend-dot" style={{ background: BUDGET_COLOR }} />
          <span>Budget</span>
        </div>
        <div className="legend-item">
          <div className="legend-dot" style={{ background: ACTUAL_COLOR }} />
          <span>Actuals</span>
        </div>
      </div>

      {isEmpty ? (
        <EmptyState height={195} />
      ) : (
        <ResponsiveContainer width="100%" height={195}>
          <BarChart
            data={data.data}
            margin={{ top: 5, right: 10, left: 5, bottom: 5 }}
            barCategoryGap="25%"
            barGap={3}
            tabIndex={-1}
            style={{ cursor: onDrillDown ? "pointer" : "default" }}
            onClick={(chartData: any) => {
              if (onDrillDown && chartData?.activeLabel) {
                onDrillDown(chartData.activeLabel);
              }
            }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#DFE2E7"
              strokeOpacity={0.3}
              vertical={false}
            />
            <XAxis
              dataKey="quarter"
              tick={{ fontSize: 12, fill: "#64748b" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#94a3b8" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `$${v}M`}
              width={50}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
            <Bar dataKey="budget" name="Budget" fill={BUDGET_COLOR} radius={[3, 3, 0, 0]} opacity={0.85} />
            <Bar dataKey="actual" name="Actuals" fill={ACTUAL_COLOR} radius={[3, 3, 0, 0]} opacity={0.9} />
          </BarChart>
        </ResponsiveContainer>
      )}

      <div className="net-variance-row">
        <span className="net-variance-label-text">Net Variance:</span>
        <div className="net-variance-right">
          <span className={`net-variance-value${data.netVariance.direction === "positive" ? "" : " net-variance-value--negative"}`}>
            {data.netVariance.value} ({data.netVariance.percentage})
          </span>
          <span className="net-variance-arrow">→</span>
        </div>
      </div>
    </div>
  );
};

export default VarianceChart;
